import { BlueprintFamilyMomentumApiItem } from '../../../../api/blueprintFamilies/momentum'
import { BlueprintFamilyRollupApiItem } from '../../../../api/blueprintFamilies/rollup'
import { DensityChange } from '../../../../api/blueprintFamilies/trendsBatch'
import { BlueprintFamilyApiItem, BlueprintFamilyEventTypeMembership } from '../../../../api/core'
import { ConventionalCategory } from '../../../genre-taxonomy/types/GenreTaxonomy'
import { MarketLevelBlueprintListItem } from '../../types/MarketLevelBlueprintListItem'
import { DEFAULT_TAG_GROUP_COLOR, TagGroupInfo } from './tagGroups'

// ---------------------------------------------------------------------------
// Shared enums / unions for the Market Catalogue browser (browse-v2).
// ---------------------------------------------------------------------------

export type CatalogueGranularity = 'eventCategory' | 'eventType' | 'blueprint'
export type CatalogueViewMode = 'list' | 'chart'
// The metric lens shared by the ranked list and the trend charts:
// 'adoption' = share of the segment that ran the blueprint inside the selected
// window, 'density' = live events running at once per day per adopting game, 'activeEvents' = live
// event instances running at the same time across the segment (a count of
// overlapping instances, not of distinct games).
export type CatalogueMetricKey = 'adoption' | 'density' | 'activeEvents'
export type CatalogueListSort = CatalogueMetricKey
// The lenses offered in the UI and accepted from the URL. 'activeEvents' is held back
// for 1.0 (its series is empty almost everywhere and it overlaps density); adding it
// back here restores the tab, the lens and the hover-card tile.
export const CATALOGUE_METRIC_KEYS: CatalogueMetricKey[] = ['adoption', 'density']
export const isCatalogueMetricOffered = (metric: CatalogueMetricKey): boolean => CATALOGUE_METRIC_KEYS.includes(metric)
// Chart titles for lines that are blueprint families, per metric lens. Passed as the
// chart's `title` wherever the family lines are the grouping (the main view's Blueprint
// Family level, the event type page's split), the way the event group and event type
// charts name their own lines.
export const BLUEPRINT_FAMILY_CHART_TITLE_KEYS: Record<CatalogueMetricKey, string> = {
  adoption: 'market-analytics:blueprint_trends_adoption_by_family_title',
  density: 'market-analytics:blueprint_trends_density_by_family_title',
  activeEvents: 'market-analytics:blueprint_trends_active_events_by_family_title',
}
export type CatalogueWindow = '6mo' | '12mo' | '24mo'
// Every window the toggles offer, in display order. A `win` param outside this list
// (e.g. a bookmarked '3mo') parses back to the default.
export const CATALOGUE_WINDOWS: CatalogueWindow[] = ['6mo', '12mo', '24mo']
// Number of months a window spans ('12mo' -> 12), the `{{months}}` of the translated labels.
export const catalogueWindowMonths = (window: CatalogueWindow): number => parseInt(window, 10)
// The window applied when the URL has no (or an invalid) `win` param. Omitted from
// generated URLs, so links for any other window must carry `win` explicitly.
export const DEFAULT_CATALOGUE_WINDOW: CatalogueWindow = '12mo'
// Column sort of the ranked-list tree ('metric' = the active metric lens
// column, 'change' = the momentum/delta column). Distinct from
// CatalogueListSort, which picks WHICH metric the lens shows.
export type CatalogueTreeSortKey = 'metric' | 'change'
export type CatalogueTreeSortDirection = 'asc' | 'desc'

// Whether the ranked list's Change column (header, cells, sort option and the hover
// card's change tile) is shown for a lens. Every lens has one: the adoption change on
// Adoption (and Active events), the density change on Density. Kept as the single switch
// so a lens can drop the column without touching the table.
export const changeColumnSupportedForLens = (_lens: CatalogueMetricKey): boolean => true

// ---------------------------------------------------------------------------
// Compact URL encoding for the `exp` (expanded tree nodes) param.
//
// Node ids are 24-hex-char Mongo ObjectIds, or `hexId:hexId` composites (see
// buildCatalogueTree). Raw hex is bulky in a CSV param, so each hex half is
// re-encoded as URL-safe base64 (24 hex chars = 12 bytes = exactly 16 base64
// chars, so no padding). Composite halves are joined with '.' in the URL —
// unlike the internal ':', URLSearchParams leaves '.' unescaped (no %3A noise).
// ---------------------------------------------------------------------------

const OBJECT_ID_HEX = /^[0-9a-f]{24}$/
const OBJECT_ID_B64 = /^[A-Za-z0-9_-]{16}$/
const EXP_URL_SEPARATOR = '.'

function hexToBase64Url(hex: string): string {
  let binary = ''
  for (let i = 0; i < hex.length; i += 2) binary += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16))
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64UrlToHex(b64: string): string | null {
  try {
    const binary = atob(b64.replace(/-/g, '+').replace(/_/g, '/'))
    let hex = ''
    for (let i = 0; i < binary.length; i++) hex += binary.charCodeAt(i).toString(16).padStart(2, '0')
    return hex
  } catch {
    return null
  }
}

// Node id → URL token. Ids that aren't (composites of) 24-char hex ObjectIds
// are passed through verbatim (future-proofing for non-ObjectId node ids).
export function encodeExpandedNodeId(id: string): string {
  const parts = id.split(':')
  if (parts.every((p) => OBJECT_ID_HEX.test(p))) return parts.map(hexToBase64Url).join(EXP_URL_SEPARATOR)
  return id
}

// URL token → node id, or null when the token should be dropped. Base64url
// tokens are decoded with a defensive round-trip check; raw-hex tokens (links
// from the pre-encoding format, or hand-mangled values) are dropped silently;
// anything else is a verbatim-encoded id and passes through unchanged.
export function decodeExpandedNodeId(token: string): string | null {
  const parts = token.split(EXP_URL_SEPARATOR)
  if (parts.every((p) => OBJECT_ID_B64.test(p))) {
    const hexes = parts.map(base64UrlToHex)
    if (hexes.every((h, i) => h !== null && hexToBase64Url(h) === parts[i])) return hexes.join(':')
    return null
  }
  // A token containing the separator must be a fully valid encoded composite;
  // a non-base64url half means a mangled value.
  if (parts.length > 1) return null
  if (token.split(':').every((p) => OBJECT_ID_HEX.test(p))) return null
  return token
}

// Maps the catalogue window union to the trends endpoint `windowDays` values.
// The backend validates against an allowlist of 30/90/180/360/720 and 400s on
// anything else, so the three windows have to land on members of that set.
export type CatalogueWindowDays = 180 | 360 | 720

export const WINDOW_TO_DAYS: Record<CatalogueWindow, CatalogueWindowDays> = {
  '6mo': 180,
  '12mo': 360,
  '24mo': 720,
}

// ---------------------------------------------------------------------------
// Metric helpers
// ---------------------------------------------------------------------------

// The one adoption display format in Live Event Analytics: a whole-number percent,
// rounded to nearest (86/93 = 92.47% → "92%"). Every adoption surface (catalogue table
// and hover card, level stats card, child-type/blueprint tables, trends chart hover and
// axis) goes through this so the same share never reads two ways.
export function formatAdoptionPct(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '—'
  return `${Math.round(rate * 100)}%`
}

// Per-adopting-game density of a niche family can sit well below 0.01, where "0.00" would
// read as no activity at all; those values keep two significant digits instead.
export function formatDensity(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const fixed = value.toFixed(2)
  if (value <= 0 || Number(fixed) !== 0) return fixed
  return value.toFixed(Math.min(20, 1 - Math.floor(Math.log10(value))))
}

// Concurrent live event instances are whole counts; a window mean is rounded
// back to one so the list, the chart axis and the hover all say the same thing.
export function formatActiveEvents(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return Math.round(value).toLocaleString()
}

// Formats a value of the active metric lens for display.
export function formatMetricValue(metric: CatalogueMetricKey, value: number | null | undefined): string {
  if (metric === 'density') return formatDensity(value)
  if (metric === 'activeEvents') return formatActiveEvents(value)
  return formatAdoptionPct(value)
}

const MINUS_SIGN = '\u2212'
const PLUS_MINUS_SIGN = '\u00B1'

// "+46" / "−3" / "±0" — the explicit-sign integer every change figure uses.
export function formatSignedInt(value: number): string {
  if (value === 0) return `${PLUS_MINUS_SIGN}0`
  return `${value > 0 ? '+' : MINUS_SIGN}${Math.abs(value)}`
}

// Change in an adoption share, in percentage points: "+46%". The explicit sign
// is what marks the number as a movement rather than a level; the "pt" suffix it
// used to carry made every reader work out that a percentage point is not a
// percent, and the hover states the definition anyway (2026-09-08 review).
export function formatAdoptionDelta(pp: number): string {
  return `${formatSignedInt(pp)}%`
}

// Percentage-point change of the adoption share — one rounding rule for every surface.
//
// The denominator is the COHORT (`cohortGamesCount`): the games in scope tracked on or
// before the selected period's first day. Momentum (the period's last month minus its
// first month) is counted over that cohort, so dividing by any other population mixes
// two populations and recreates the >100pt readings the momentum endpoint exists to fix.
// Since the BE moved list / detail / rollup onto the same cohort, the Adoption tile and
// this Change figure share one denominator.
//
// Null when the cohort is unknown or zero (the caller then shows the raw games count).
export function adoptionChangePp(deltaGames: number | null, cohortGamesCount: number | null): number | null {
  if (deltaGames === null || cohortGamesCount === null || cohortGamesCount <= 0) return null
  return Math.round((deltaGames / cohortGamesCount) * 100)
}

// Which figure the ranked list's Change column measures. It follows the metric lens: on
// the density lens it is the movement of density, on every other lens the movement of
// adoption (the held-back concurrent-instances lens has no change of its own and keeps
// the adoption one it always showed).
export type CatalogueChangeKind = 'adoption' | 'density'

export function catalogueChangeKind(metric: CatalogueMetricKey): CatalogueChangeKind {
  return metric === 'density' ? 'density' : 'adoption'
}

// The label of the row hover card's change tile, so it names the figure the lens
// measures. The column header stays "Change" on every lens (its tooltip names the figure).
export function catalogueChangeLabelKey(metric: CatalogueMetricKey): string {
  return catalogueChangeKind(metric) === 'density' ? 'market-analytics:detail_stat_delta_density_label' : 'market-analytics:catalogue_change'
}

// Density as the density column shows it, as a number: the reader's own two-decimal (or,
// for a niche family, two-significant-digit) reading of the value.
function displayedDensity(value: number): number {
  return Number(formatDensity(value))
}

// Change in density between the period's first and last month, in events per adopting game: the
// difference of the two values AS DISPLAYED, so a reader who subtracts the column's
// figures gets the number in the Change cell (2.36 − 1.94 reads +0.42, never the +0.41 a
// difference of the unrounded 2.356 and 1.941 would round to), and two months that read
// the same read as unchanged.
//
// Null (shown as "—", sorted like a missing value) when either end's density is absent,
// null or 0. Density is per adopting game and the backend reports 0 for a month with no
// adopters, so a 0 end has no density to compare and differencing against it would read as
// a huge jump. Adopter counts are deliberately not consulted: the adoption series counts a
// fixed cohort, while density counts the segment's own adopters, so a game outside the
// cohort can give a positive density next to an adoption of 0.
export function densityChangeDelta(change: DensityChange | null | undefined): number | null {
  if (!change) return null
  if (!Number.isFinite(change.first) || !(change.first > 0)) return null
  if (!Number.isFinite(change.last) || !(change.last > 0)) return null
  const delta = displayedDensity(change.last) - displayedDensity(change.first)
  // Subtracting decimals leaves float residue (0.1 + 0.2 style); 12 significant digits is
  // far past anything the column shows.
  return Number(delta.toPrecision(12))
}

// "+0.42" / "−0.30" / "±0.00" — a signed change in events per adopting game, at the density
// column's own precision. The sign marks it as a movement rather than a level, as for
// the adoption change.
export function formatDensityDelta(delta: number): string {
  if (delta === 0) return `${PLUS_MINUS_SIGN}0.00`
  return `${delta > 0 ? '+' : MINUS_SIGN}${formatDensity(Math.abs(delta))}`
}

// ---------------------------------------------------------------------------
// Tree model
// ---------------------------------------------------------------------------

export interface CatalogueNodeMetric {
  // Windowed adoption ratio in [0, 1]. At group level this comes from the
  // windowed rollup endpoint; at blueprint level it is derived as
  // `adoptionGames / cohortSize` (see enrichTree).
  adoptionPct: number | null
  // Windowed live-event density (events running at once per day per adopting game). Sourced
  // from the backend trends batch (`density`, its last month) via enrichTree; null until
  // that data loads. NOT the cumulative liveEventsCount / gamesCount proxy,
  // which over-counts long-ended events and inflates the number.
  density: number | null
  // Density of the period's first and last month, from the same series as `density`:
  // the pair the Change column compares on the density lens. Null when the series lacks
  // either month (or the trends data hasn't loaded).
  densityChange: DensityChange | null
  // Windowed mean of live event instances running at the same time across the
  // segment (backend `activeLiveEventsCount` daily series, boiled down to a
  // scalar via enrichTree). Instances are additive across blueprints — unlike
  // adopting games — so group nodes sum their children. Null until loaded.
  activeEvents: number | null
  // Distinct blueprint families under the node. A family placed under two event
  // types of one category still counts once at category level.
  blueprintCount: number
  // Distinct event types among the node's blueprints. Meaningful on
  // event-category nodes (drives the "N event types" subtitle); on event-type
  // and blueprint nodes it is trivially 1.
  eventTypeCount: number
  // Distinct adopting games (numerator of the "X / Y games" fraction). Group
  // nodes use the rollup `distinctGamesCount`; blueprint leaves use `gamesCount`.
  adoptionGames: number | null
  // The cohort behind BOTH the adoption fraction and the percentage-point change:
  // games in scope tracked on or before the selected period's first day
  // (`cohortGamesCount`). Resolved once per request and shared across nodes —
  // Adoption and Change are no longer measured over different populations.
  cohortSize: number | null
  // Net change in adopting games over the selected period (its last month minus its
  // first month), from the momentum endpoint: `groupBy=FAMILY` for blueprint leaves, `groupBy=EVENT_GROUP` /
  // `EVENT_TYPE` for the group rows. Summing child momentum is not an option — it
  // counts a game once per blueprint it adopted (Monetization reads +28 games summed
  // versus +0 over the union of its games), which is the "102%" a reviewer saw.
  deltaGames: number | null
  // Games that FIRST adopted within the window (absolute recent-wave size, NOT
  // the net momentum delta): `newGames.length` from the list row on blueprint
  // leaves, from the rollup row on group nodes. Cohort-bounded, and a coarser
  // node can legitimately report FEWER than one of its children.
  newAdopters: number | null
}

export interface CatalogueNode {
  id: string
  kind: CatalogueGranularity
  name: string
  colorHex: string
  metric: CatalogueNodeMetric
  // Present only on blueprint leaves; drives the row's link to the blueprint page.
  blueprintId?: string
  // Present only on blueprint leaves: the blueprint is discontinued (skull badge).
  discontinued?: boolean
  // Higher-level context for blueprint leaves (event category + event type
  // names) so a flat "Event Blueprint" listing still reads its place in the
  // taxonomy. Populated for leaves only.
  categoryName?: string
  eventTypeName?: string
  children?: CatalogueNode[]
}

export interface CatalogueFilter {
  eventCategoryIds: string[]
  eventTypeIds: string[]
}

export interface BuildTreeArgs {
  blueprints: MarketLevelBlueprintListItem[]
  tagGroups: TagGroupInfo[]
  granularity: CatalogueGranularity
  filter: CatalogueFilter
  getBlueprintName: (bp: MarketLevelBlueprintListItem) => string
  getEventTypeName: (tagId: string) => string
}

// Group-node metric from its placements. `adoptionPct` is a games-weighted mean of
// windowed adoption across the blueprints that carry an adoptionRate — a stand-in
// until enrichTree replaces it with the group's own rollup row. `items` holds one
// entry per (family, event type) placement, so the blueprint count dedupes by id.
function aggregateMetric(items: MarketLevelBlueprintListItem[]): CatalogueNodeMetric {
  let adoptionWeightSum = 0
  let adoptionWeighted = 0
  const eventTypes = new Set<string>()
  const families = new Set<string>()
  for (const bp of items) {
    families.add(bp.id)
    eventTypes.add(bp.tagId || '—')
    const rate = bp.adoptionRate?.windowed
    if (rate !== undefined && rate !== null) {
      const weight = bp.numberOfGames || 1
      adoptionWeighted += rate * weight
      adoptionWeightSum += weight
    }
  }
  return {
    adoptionPct: adoptionWeightSum === 0 ? null : adoptionWeighted / adoptionWeightSum,
    // Filled in by enrichTree from the backend's windowed trend scalars.
    density: null,
    densityChange: null,
    activeEvents: null,
    blueprintCount: families.size,
    eventTypeCount: eventTypes.size,
    adoptionGames: null,
    cohortSize: null,
    deltaGames: null,
    newAdopters: null,
  }
}

// `perPlacement` selects the count shown on the leaf: under an event type the
// row shows games that ran the family under that type; at blueprint granularity
// the family total. Both cover the period's last month, so a placement's count never
// exceeds the family total, and a family with a single membership has the same
// number both ways.
function blueprintLeaf(
  bp: MarketLevelBlueprintListItem,
  name: string,
  colorHex: string,
  categoryName?: string,
  eventTypeName?: string,
  perPlacement = true
): CatalogueNode {
  const adoptionGames = perPlacement ? bp.placementAdoptingGames ?? bp.adoptingGamesInSegment ?? null : bp.adoptingGamesInSegment ?? null
  return {
    // One node per placement: a family listed under two event types has two
    // leaves with distinct ids but the same `blueprintId`. At blueprint
    // granularity the family appears once, so it keeps its own id.
    id: perPlacement ? bp.placementKey ?? bp.id : bp.id,
    kind: 'blueprint',
    name,
    colorHex,
    blueprintId: bp.id,
    discontinued: bp.discontinued,
    categoryName,
    eventTypeName,
    metric: {
      adoptionPct: bp.adoptionRate?.windowed ?? null,
      // Filled in by enrichTree from the backend's windowed trend scalars.
      density: null,
      densityChange: null,
      activeEvents: null,
      blueprintCount: 1,
      eventTypeCount: 1,
      // Windowed, cohort-scoped adopting games (backend `distinctGamesCount`).
      // Used as the adoption numerator so `adoptionGames / cohortSize` can never
      // exceed 100% (the whole-market `gamesCount` did, e.g. 38 / 10 = 380%).
      adoptionGames,
      cohortSize: null,
      deltaGames: null,
      // Windowed first-adopter count from the list endpoint (`newGames.length`).
      newAdopters: bp.newAdoptersInWindow ?? null,
    },
  }
}

// Collapses per-event-type placements back to one item per family, keeping the
// first placement (most-used type). Used at blueprint granularity so a family
// spanning two types is listed once with its deduplicated total.
function uniqueFamilies(items: MarketLevelBlueprintListItem[]): MarketLevelBlueprintListItem[] {
  const seen = new Set<string>()
  const out: MarketLevelBlueprintListItem[] = []
  for (const bp of items) {
    if (seen.has(bp.id)) continue
    seen.add(bp.id)
    out.push(bp)
  }
  return out
}

// Builds the granularity-driven tree consumed by RankedBlueprintTree.
export function buildCatalogueTree({ blueprints, tagGroups, granularity, filter, getBlueprintName, getEventTypeName }: BuildTreeArgs): CatalogueNode[] {
  const colorByGroup = new Map(tagGroups.map((g) => [g.id, g.colorHex]))
  const nameByGroup = new Map(tagGroups.map((g) => [g.id, g.name]))

  const categoryFilter = filter.eventCategoryIds.length > 0 ? new Set(filter.eventCategoryIds) : null
  const typeFilter = filter.eventTypeIds.length > 0 ? new Set(filter.eventTypeIds) : null

  const filtered = blueprints.filter((bp) => {
    if (!bp.tagGroupId) return false
    if (categoryFilter && !categoryFilter.has(bp.tagGroupId)) return false
    if (typeFilter && (!bp.tagId || !typeFilter.has(bp.tagId))) return false
    return true
  })

  const colorFor = (groupId: string) => colorByGroup.get(groupId) || DEFAULT_TAG_GROUP_COLOR
  const categoryNameFor = (groupId: string) => nameByGroup.get(groupId) ?? groupId
  const leafFor = (bp: MarketLevelBlueprintListItem, color: string) =>
    blueprintLeaf(bp, getBlueprintName(bp), color, categoryNameFor(bp.tagGroupId), bp.tagId ? getEventTypeName(bp.tagId) : undefined)

  if (granularity === 'blueprint') {
    return uniqueFamilies(filtered).map((bp) =>
      blueprintLeaf(bp, getBlueprintName(bp), colorFor(bp.tagGroupId), categoryNameFor(bp.tagGroupId), bp.tagId ? getEventTypeName(bp.tagId) : undefined, false)
    )
  }

  if (granularity === 'eventType') {
    const byType = new Map<string, MarketLevelBlueprintListItem[]>()
    for (const bp of filtered) {
      const key = bp.tagId || '—'
      if (!byType.has(key)) byType.set(key, [])
      byType.get(key)!.push(bp)
    }
    return Array.from(byType.entries()).map(([tagId, items]) => {
      const groupId = items[0]?.tagGroupId ?? ''
      const color = colorFor(groupId)
      return {
        id: tagId,
        kind: 'eventType' as const,
        name: getEventTypeName(tagId),
        colorHex: color,
        categoryName: categoryNameFor(groupId),
        metric: aggregateMetric(items),
        children: items.map((bp) => leafFor(bp, color)),
      }
    })
  }

  // granularity === 'eventCategory' → category › event type › blueprint
  const byCategory = new Map<string, MarketLevelBlueprintListItem[]>()
  for (const bp of filtered) {
    const key = bp.tagGroupId
    if (!byCategory.has(key)) byCategory.set(key, [])
    byCategory.get(key)!.push(bp)
  }

  return Array.from(byCategory.entries()).map(([groupId, items]) => {
    const color = colorFor(groupId)
    const byType = new Map<string, MarketLevelBlueprintListItem[]>()
    for (const bp of items) {
      const key = bp.tagId || '—'
      if (!byType.has(key)) byType.set(key, [])
      byType.get(key)!.push(bp)
    }
    const typeChildren: CatalogueNode[] = Array.from(byType.entries()).map(([tagId, typeItems]) => ({
      id: `${groupId}:${tagId}`,
      kind: 'eventType' as const,
      name: getEventTypeName(tagId),
      colorHex: color,
      categoryName: categoryNameFor(groupId),
      metric: aggregateMetric(typeItems),
      children: typeItems.map((bp) => leafFor(bp, color)),
    }))
    return {
      id: groupId,
      kind: 'eventCategory' as const,
      name: nameByGroup.get(groupId) ?? groupId,
      colorHex: color,
      metric: aggregateMetric(items),
      children: typeChildren,
    }
  })
}

// ---------------------------------------------------------------------------
// Rollup enrichment
// ---------------------------------------------------------------------------

// Adopting games for one blueprint: cohort games with an event in the selected
// period's last month, as reported by `distinctGamesCount`. Every multi-blueprint surface (tree,
// sibling rail, hierarchy menus) reads it through here so they all show the
// same figure for the same blueprint.
//
// Null when the backend omits the field. There is deliberately no fallback to
// `gamesCount`: that is the all-time count and so always ≥ this one, which
// would put two different scales in one list without saying so. Callers render
// nothing for a null instead.
export function adoptingGamesOf(blueprint: Pick<BlueprintFamilyApiItem, 'distinctGamesCount'>): number | null {
  return blueprint.distinctGamesCount ?? null
}

// Event type id → event category (tag group) id, read from the event tag groups the pages
// already load: each group's `tags` lists the event types filed under it. Used by the Data
// Glossary only, to file a curated family under the categories of its allowed types.
export type EventTypeCategoryLookup = ReadonlyMap<string, string>

export function buildEventTypeCategoryLookup(tagGroups: readonly { id: string; tags?: readonly string[] }[] | undefined): EventTypeCategoryLookup {
  const lookup = new Map<string, string>()
  for (const group of tagGroups ?? []) {
    for (const typeId of group.tags ?? []) {
      if (!lookup.has(typeId)) lookup.set(typeId, group.id)
    }
  }
  return lookup
}

type MembershipSource = Pick<BlueprintFamilyApiItem, 'eventTypes' | 'allowedEventTypeIds'>

// A family's allowed event types as placements, whether or not it ran them in the window or
// on the list's platform (mobile only unless `includePcConsole`). `eventTypes` only reports
// what ran in that window and platform, so a family whose runs are all on PC, or all in the
// month in progress, has none even though analysts have curated its types. A type that did
// run keeps its own entry (and so its window count); one that didn't is listed with 0.
// A type whose category can't be resolved is skipped rather than filed under a guessed one.
//
// Not for Live Event Analytics: there a family with no events in the selected window is
// left out of every category (see `familyEventTypeMemberships`).
function allowedEventTypeMemberships(blueprint: MembershipSource, categoryIdByTypeId: EventTypeCategoryLookup): BlueprintFamilyEventTypeMembership[] {
  const allowed = new Set(blueprint.allowedEventTypeIds ?? [])
  // Types it ran keep their window entry (and order, most games first), taking the category
  // from the lookup when the entry carries none; the ones it didn't follow in the order
  // analysts listed them.
  const memberships: BlueprintFamilyEventTypeMembership[] = []
  const placedTypeIds = new Set<string>()
  for (const membership of blueprint.eventTypes ?? []) {
    if (!allowed.has(membership.typeId) || placedTypeIds.has(membership.typeId)) continue
    const categoryId = membership.categoryId || categoryIdByTypeId.get(membership.typeId)
    if (!categoryId) continue
    placedTypeIds.add(membership.typeId)
    memberships.push({ ...membership, categoryId })
  }
  for (const typeId of allowed) {
    if (placedTypeIds.has(typeId)) continue
    const categoryId = categoryIdByTypeId.get(typeId)
    if (categoryId) memberships.push({ typeId, categoryId, distinctGamesCount: 0 })
  }
  return memberships
}

// Every event type a family belongs to, ordered by whole-period distinct games desc
// (not by each entry's last-month `distinctGamesCount`).
// All placement logic (tree, type page, hierarchy crumbs) goes through here.
//
// An empty list is a real answer, not a gap to paper over: it means the family ran no
// events inside the window (or none on mobile, the only platform the list is asked for),
// so it has no place in the tree for this scope and is left out of it. Live Event
// Analytics is a view of activity in the selected window, so a family is never listed
// under a category it has no in-window events for — not even one analysts curated for it.
// The Data Glossary is the reference that files families by their curated types instead
// (see `familyGlossaryEventTypeMemberships`). There is deliberately no fallback to a
// single "most used" type either: the field that used to provide one was an all-time
// argmax that disagreed with this ordering for 17 of 154 families on prod, 11 of them
// flipping category.
//
// Once analysts have curated a family's allowed event types, only the allowed
// entries place it. Odd-case events still run under other types, and still count
// towards the family's own figures, but must never file the family under those
// types or offer it as an option there. An uncurated family keeps every entry.
export function familyEventTypeMemberships(blueprint: MembershipSource): BlueprintFamilyEventTypeMembership[] {
  const eventTypes = blueprint.eventTypes ?? []
  const allowedEventTypeIds = blueprint.allowedEventTypeIds ?? []
  if (allowedEventTypeIds.length === 0) return eventTypes
  const allowed = new Set(allowedEventTypeIds)
  return eventTypes.filter((membership) => allowed.has(membership.typeId))
}

// Where the Data Glossary files a family. The glossary is a reference, not a window of
// activity, so a curated family belongs under the category of EVERY allowed type, not only
// the ones it happened to run on mobile in the default window. Falls back to the window
// placement when the family is uncurated or none of its allowed types resolves to a category.
export function familyGlossaryEventTypeMemberships(
  blueprint: MembershipSource,
  categoryIdByTypeId: EventTypeCategoryLookup
): BlueprintFamilyEventTypeMembership[] {
  const curated = allowedEventTypeMemberships(blueprint, categoryIdByTypeId)
  return curated.length > 0 ? curated : familyEventTypeMemberships(blueprint)
}

// True when the family is placed under more than one event type.
export function familyHasMultipleEventTypes(blueprint: MembershipSource): boolean {
  return familyEventTypeMemberships(blueprint).length > 1
}

// Membership of `blueprint` in `eventTypeId`, or null when the family is not
// placed under that type.
export function familyMembershipInEventType(blueprint: MembershipSource, eventTypeId: string): BlueprintFamilyEventTypeMembership | null {
  return familyEventTypeMemberships(blueprint).find((m) => m.typeId === eventTypeId) ?? null
}

// Sort key for the popularity ordering the rail and the hierarchy menus share.
// A blueprint with no adopting-games figure sorts below every blueprint that has
// one, rather than tying with a genuine zero.
export function adoptingGamesSortKey(blueprint: Pick<BlueprintFamilyApiItem, 'distinctGamesCount'>): number {
  return adoptingGamesOf(blueprint) ?? -1
}

// The adoption figures of an event category or event type rollup row: games running
// any event of it, whether or not the event's blueprint belongs to a family. This is
// the population the row's trends series reconciles with, so a page tile, its child
// table and the chart agree. Blueprint family rows (the ranked list, the family
// tables) count the family's own events instead.
export function rollupRowAdoption(row: BlueprintFamilyRollupApiItem | undefined): { adoptingGames: number | null; adoptionRate: number | undefined } {
  if (!row) return { adoptingGames: null, adoptionRate: undefined }
  return { adoptingGames: row.distinctGamesCount ?? null, adoptionRate: row.adoptionRate?.windowed }
}

// The cohort behind every adoption fraction in the tool: games tracked on or before the
// selected period's first day. Unlike the segment size it replaced, it moves with the
// window (a longer window starts earlier, so fewer games were tracked by then). The
// response states it outright as `cohortGamesCount`, so read it and stop there.
//
// The two fallbacks are transitional only, for environments whose rollup predates the
// cohort contract: the removed `segmentGamesCount`, then back-solving it from the
// adoption rate. Prefer the reported value at every call site — back-solving is lossy at
// small rates and silently wrong if `adoptionRate` ever changes meaning again, which it
// already did once when adoption went windowed. Delete both once every environment we
// ship against returns `cohortGamesCount`.
export function resolveCohortSize(rollup: BlueprintFamilyRollupApiItem[]): number | null {
  const reported = rollup.find((row) => typeof row.cohortGamesCount === 'number')?.cohortGamesCount
  if (typeof reported === 'number' && reported > 0) return reported
  const legacy = rollup.find((row) => typeof row.segmentGamesCount === 'number')?.segmentGamesCount
  if (typeof legacy === 'number' && legacy > 0) return legacy
  return deriveSegmentSize(rollup)
}

// The rollup endpoint reports `adoptionRate.windowed = distinctGamesCount / cohortSize`,
// so the denominator is recoverable. Both inputs are windowed by the same `windowDays`,
// so the quotient survives the windowed-adoption release. We take the row with the
// largest windowed rate to minimise rounding error.
//
// Fallback only — see `resolveCohortSize`. Delete once every environment we
// ship against returns `cohortGamesCount`.
export function deriveSegmentSize(rollup: BlueprintFamilyRollupApiItem[]): number | null {
  let segmentSize: number | null = null
  let bestWindowed = 0
  for (const row of rollup) {
    const windowed = row.adoptionRate?.windowed
    if (!windowed || windowed <= 0) continue
    if (windowed > bestWindowed) {
      bestWindowed = windowed
      segmentSize = Math.round(row.distinctGamesCount / windowed)
    }
  }
  return segmentSize
}

// Extracts the event-type tag id from a node id. In event-category granularity
// the type node id is `${groupId}:${tagId}`; in event-type granularity it is the
// bare tag id. Exported so the tree rows can link an event-type node to its own
// page without re-deriving the composite id format.
export function eventTypeIdFromNodeId(nodeId: string): string {
  const sep = nodeId.indexOf(':')
  return sep >= 0 ? nodeId.slice(sep + 1) : nodeId
}

// Inputs for the single-pass tree enrichment. Bundling the maps keeps the call
// site tidy and lets the whole tree be enriched in one traversal.
export interface EnrichTreeOptions {
  // Windowed rollup keyed by group id (event categories) and by event-type id.
  rollupMaps: {
    group: Map<string, BlueprintFamilyRollupApiItem>
    type: Map<string, BlueprintFamilyRollupApiItem>
  }
  // Per-adopting-game density of the period's last month (last point of the `density`
  // series), not a window-wide figure, keyed by blueprint id (see
  // getBlueprintFamilyDensityScalars).
  densityMap: Map<string, number | null>
  // Same, for event-category and event-type rows, from the group trends endpoints (the
  // group's own series, per adopting game). Keyed by category id / event-type
  // id. Group rows read that series rather than summing their children's density.
  // Optional; group rows show a dash without it.
  groupDensityMap?: Map<string, number | null>
  // The first and last month of each density series, keyed like `densityMap` and
  // `groupDensityMap`. Optional; rows without an entry have no density change.
  densityChangeMap?: Map<string, DensityChange | undefined>
  groupDensityChangeMap?: Map<string, DensityChange | undefined>
  // Windowed mean of concurrently active event instances keyed by blueprint id
  // (same scalar request as the density). Optional: only fetched under the
  // concurrent-instances lens.
  activeEventsMap?: Map<string, number | null>
  // Per-blueprint net change in adopting games over the selected period (its last
  // month minus its first month); optional so the tree can be enriched before
  // momentum lands.
  momentumMap?: Map<string, MomentumStat>
  // Same, for event-category and event-type rows, from the momentum endpoint's
  // grouped modes. Keyed by group id / event-type id. Optional; group rows show a
  // dash without it.
  groupMomentumMap?: Map<string, MomentumStat>
  // The request's cohort — games in scope tracked on or before the selected period's
  // first day. Denominator of both the adoption fraction and the change.
  cohortSize: number | null
}

// One momentum reading with the cohort it was counted over. The two always travel
// together: the count is meaningless as a share without its own denominator, and
// pairing it with a different population is the bug `cohortGamesCount` was added to fix.
export interface MomentumStat {
  deltaGames: number
  cohortGamesCount: number
}

// Index a momentum response by row id. Every caller pairs the two fields the same
// way, so the pairing lives here rather than in each page's `useMemo`.
export function toMomentumMap(rows: BlueprintFamilyMomentumApiItem[] | undefined): Map<string, MomentumStat> {
  return new Map((rows ?? []).map((row) => [row.id, { deltaGames: row.momentum, cohortGamesCount: row.cohortGamesCount }]))
}

// Single-pass tree enrichment. In ONE traversal it:
//  • Merges windowed rollup data (adoption %, distinct adopting games) into the
//    group nodes and derives per-blueprint adoption from the windowed adopting
//    games + cohort size.
//  • Folds momentum (net change in adopting games between the period's first and
//    last month) into the
//    `deltaGames` "Change" column, alongside the cohort it was counted over. Group
//    nodes take the endpoint's grouped modes rather than summing their children.
//  • Fills in the ranked-list density from the backend's windowed per-game scalar —
//    blueprint leaves take theirs from `densityMap`, group nodes from `groupDensityMap`
//    (their own series, not a sum of their children).
//  • Fills in concurrent event instances: leaves from `activeEventsMap`, group nodes
//    sum their descendants (see sumChildMetric) since instance counts add up across
//    blueprints, adopting games do not.
// Rebuilding node identities once per data change (rather than 2-3× via chained
// passes) keeps the memoized consumers cheap. Nodes with no backend data keep a
// null density (the cell renders "—").
export function enrichTree(nodes: CatalogueNode[], options: EnrichTreeOptions): CatalogueNode[] {
  const { rollupMaps, densityMap, groupDensityMap, densityChangeMap, groupDensityChangeMap, activeEventsMap, momentumMap, groupMomentumMap, cohortSize } =
    options
  const enrich = (list: CatalogueNode[]): CatalogueNode[] =>
    list.map((node) => {
      if (node.children) {
        const children = enrich(node.children)
        let metric = node.metric
        // Group rows take the momentum endpoint's grouped modes, which dedupe games
        // across the group rather than summing child momentum.
        const groupMomentum = groupMomentumMap?.get(node.kind === 'eventType' ? eventTypeIdFromNodeId(node.id) : node.id) ?? null
        const deltaGames = groupMomentum?.deltaGames ?? null
        // The momentum row's own cohort is the same number for the same request; it is
        // only read when the rollup hasn't resolved one (transitional environments).
        const nodeCohortSize = cohortSize ?? groupMomentum?.cohortGamesCount ?? null
        // Rollup rows also carry the group's windowed new adopters, so the
        // new-adopters context reads consistently at every tree level.
        if (node.kind === 'eventCategory') {
          const row = rollupMaps.group.get(node.id)
          metric = row
            ? {
                ...metric,
                adoptionPct: row.adoptionRate.windowed,
                adoptionGames: row.distinctGamesCount,
                newAdopters: row.newGames?.length ?? metric.newAdopters,
              }
            : metric
        } else if (node.kind === 'eventType') {
          const row = rollupMaps.type.get(eventTypeIdFromNodeId(node.id))
          metric = row
            ? {
                ...metric,
                adoptionPct: row.adoptionRate.windowed,
                adoptionGames: row.distinctGamesCount,
                newAdopters: row.newGames?.length ?? metric.newAdopters,
              }
            : metric
        }
        // Density comes from the group's own series, like the detail pages' tile, so the
        // list and the page agree. Summing the children would add per-family figures
        // that have different denominators.
        const groupId = node.kind === 'eventType' ? eventTypeIdFromNodeId(node.id) : node.id
        const density = groupDensityMap?.get(groupId) ?? null
        const densityChange = groupDensityChangeMap?.get(groupId) ?? null
        const activeEvents = sumChildMetric(children, 'activeEvents')
        return { ...node, metric: { ...metric, deltaGames, cohortSize: nodeCohortSize, density, densityChange, activeEvents }, children }
      }
      // Windowed adopting games set on the leaf at build time (backend
      // `distinctGamesCount`). Stays null when the backend omits it — the
      // all-time `gamesCount` is a different scale, so the row shows an em dash
      // rather than a number the adoption share can't be read against.
      const adoptionGames = node.metric.adoptionGames
      const adoptionPct = adoptionGames !== null && cohortSize && cohortSize > 0 ? adoptionGames / cohortSize : node.metric.adoptionPct
      const leafId = node.blueprintId ?? node.id
      const momentum = momentumMap?.get(leafId) ?? null
      const density = densityMap.get(leafId) ?? null
      const densityChange = densityChangeMap?.get(leafId) ?? null
      const activeEvents = activeEventsMap?.get(leafId) ?? null
      return {
        ...node,
        metric: {
          ...node.metric,
          adoptionGames,
          adoptionPct,
          cohortSize: cohortSize ?? momentum?.cohortGamesCount ?? null,
          deltaGames: momentum?.deltaGames ?? null,
          density,
          densityChange,
          activeEvents,
        },
      }
    })
  return enrich(nodes)
}

// Concurrent instances count events across the same segment for every child, so a
// group's figure is the sum of its children's: a game running two blueprints of the
// same type has two events live. Null children are skipped; null when none has data.
function sumChildMetric(children: CatalogueNode[], key: 'activeEvents'): number | null {
  let sum = 0
  let any = false
  for (const child of children) {
    const value = child.metric[key]
    if (value === null) continue
    sum += value
    any = true
  }
  return any ? sum : null
}

// Value of the active metric lens for a node. Shared by sorting + display.
export function catalogueMetricValue(node: CatalogueNode, metric: CatalogueMetricKey): number | null {
  if (metric === 'adoption') return node.metric.adoptionPct
  if (metric === 'activeEvents') return node.metric.activeEvents
  return node.metric.density
}

// The figure the Change column shows for a node under the active lens, in the unit it
// sorts by: games for the adoption change (as before), events per adopting game for the density
// change. Null when unknown. Shared by the column's sort and its display.
export function catalogueChangeValue(node: CatalogueNode, metric: CatalogueMetricKey): number | null {
  if (catalogueChangeKind(metric) === 'density') return densityChangeDelta(node.metric.densityChange)
  return node.metric.deltaGames
}

// Sorts a node array by the active metric, descending. Nulls sort last.
export function sortNodes(nodes: CatalogueNode[], metric: CatalogueMetricKey): CatalogueNode[] {
  return [...nodes].sort((a, b) => compareCatalogueNodes(a, b, metric, 'metric', 'desc'))
}

// Compare two sibling nodes for the ranked tree. Nulls always sort last;
// `direction` only flips two present values.
export function compareCatalogueNodes(
  a: CatalogueNode,
  b: CatalogueNode,
  metric: CatalogueMetricKey,
  sortKey: CatalogueTreeSortKey,
  direction: CatalogueTreeSortDirection
): number {
  const value = (n: CatalogueNode) => (sortKey === 'change' ? catalogueChangeValue(n, metric) : catalogueMetricValue(n, metric))
  const dir = direction === 'asc' ? -1 : 1
  const av = value(a)
  const bv = value(b)
  if (av !== null && bv !== null && av !== bv) return (bv - av) * dir
  if (av === null && bv !== null) return 1
  if (bv === null && av !== null) return -1
  if (sortKey === 'change') return compareByMetricDescending(a, b, metric)
  return 0
}

// Tiebreak for the Change sort: rows whose change is equal (or both missing) keep the
// lens metric's order, largest first in either direction, so ties never fall back to
// source order.
function compareByMetricDescending(a: CatalogueNode, b: CatalogueNode, metric: CatalogueMetricKey): number {
  const am = catalogueMetricValue(a, metric)
  const bm = catalogueMetricValue(b, metric)
  if (am === null && bm === null) return 0
  if (am === null) return 1
  if (bm === null) return -1
  return bm - am
}

// Recursively sorts a tree (each level by the active metric).
export function sortTree(nodes: CatalogueNode[], metric: CatalogueMetricKey): CatalogueNode[] {
  return sortNodes(nodes, metric).map((node) => (node.children ? { ...node, children: sortTree(node.children, metric) } : node))
}

// Flattens all blueprint leaves out of the tree (used by the chart + overview).
export function collectBlueprintLeaves(nodes: CatalogueNode[]): CatalogueNode[] {
  const out: CatalogueNode[] = []
  const walk = (list: CatalogueNode[]) => {
    for (const n of list) {
      if (n.kind === 'blueprint') out.push(n)
      else if (n.children) walk(n.children)
    }
  }
  walk(nodes)
  return out
}

// Ids of every event category and event type row in the tree, as the group trends
// endpoints key them (event-type node ids are composite, so they are unwrapped). Sorted
// and de-duplicated so the request's cache key stays stable across granularity changes.
export function collectGroupIds(nodes: CatalogueNode[]): { eventGroupIds: string[]; eventTypeIds: string[] } {
  const eventGroupIds = new Set<string>()
  const eventTypeIds = new Set<string>()
  const walk = (list: CatalogueNode[]) => {
    for (const n of list) {
      if (n.kind === 'eventCategory') eventGroupIds.add(n.id)
      else if (n.kind === 'eventType') eventTypeIds.add(eventTypeIdFromNodeId(n.id))
      if (n.children) walk(n.children)
    }
  }
  walk(nodes)
  return { eventGroupIds: Array.from(eventGroupIds).sort(), eventTypeIds: Array.from(eventTypeIds).sort() }
}

// ---------------------------------------------------------------------------
// Event taxonomy aggregation (filter chip counts)
// ---------------------------------------------------------------------------

export interface EventCategoryOption {
  id: string
  name: string
  colorHex: string
  blueprintCount: number
  // Set on a kept pick's 0-count pill while the count is not known yet, so the chip hides it.
  isCountPending?: boolean
}

export interface EventTypeOption {
  id: string
  name: string
  categoryId: string
  colorHex: string
  blueprintCount: number
  isCountPending?: boolean
}

// Distinct event categories present in the data, with derived blueprint counts.
// `blueprints` holds one item per (family, event type) placement, so families are
// deduped by id per category: one placed under two types of a category counts once.
export function buildEventCategoryOptions(blueprints: MarketLevelBlueprintListItem[], tagGroups: TagGroupInfo[]): EventCategoryOption[] {
  const familiesByCategory = new Map<string, Set<string>>()
  for (const bp of blueprints) {
    if (!bp.tagGroupId) continue
    let families = familiesByCategory.get(bp.tagGroupId)
    if (!families) {
      families = new Set<string>()
      familiesByCategory.set(bp.tagGroupId, families)
    }
    families.add(bp.id)
  }
  const orderById = new Map(tagGroups.map((g) => [g.id, g.order ?? Number.MAX_SAFE_INTEGER]))
  const nameById = new Map(tagGroups.map((g) => [g.id, g.name]))
  const colorById = new Map(tagGroups.map((g) => [g.id, g.colorHex]))
  return Array.from(familiesByCategory.entries())
    .map(([id, families]) => ({
      id,
      name: nameById.get(id) ?? id,
      colorHex: colorById.get(id) ?? DEFAULT_TAG_GROUP_COLOR,
      blueprintCount: families.size,
    }))
    .sort((a, b) => {
      // Highest blueprint count first; fall back to the group `order` so equal
      // counts stay deterministic.
      if (b.blueprintCount !== a.blueprintCount) return b.blueprintCount - a.blueprintCount
      const oa = orderById.get(a.id) ?? Number.MAX_SAFE_INTEGER
      const ob = orderById.get(b.id) ?? Number.MAX_SAFE_INTEGER
      return oa - ob
    })
}

// Distinct event types, optionally scoped to the selected event categories.
export function buildEventTypeOptions(
  blueprints: MarketLevelBlueprintListItem[],
  tagGroups: TagGroupInfo[],
  selectedCategoryIds: string[],
  getEventTypeName: (tagId: string) => string
): EventTypeOption[] {
  const colorById = new Map(tagGroups.map((g) => [g.id, g.colorHex]))
  const categorySet = selectedCategoryIds.length > 0 ? new Set(selectedCategoryIds) : null
  const byType = new Map<string, { categoryId: string; count: number }>()
  for (const bp of blueprints) {
    if (!bp.tagId || !bp.tagGroupId) continue
    if (categorySet && !categorySet.has(bp.tagGroupId)) continue
    const existing = byType.get(bp.tagId)
    if (existing) existing.count += 1
    else byType.set(bp.tagId, { categoryId: bp.tagGroupId, count: 1 })
  }
  return Array.from(byType.entries())
    .map(([id, { categoryId, count }]) => ({
      id,
      name: getEventTypeName(id),
      categoryId,
      colorHex: colorById.get(categoryId) ?? DEFAULT_TAG_GROUP_COLOR,
      blueprintCount: count,
    }))
    .sort((a, b) => b.blueprintCount - a.blueprintCount || a.name.localeCompare(b.name))
}

// Pills for the picks the data offers none for. A pick no family answers in the current
// window or segment (a shared link, or a pill chosen under another window) stays applied: it is
// kept in its row as a selected pill with a count of 0, so the reader can see why the list is
// empty and clear it, and switching the window back restores the view. Pills nobody picked
// are never added, so zero-count pills stay hidden otherwise. Picks the taxonomy doesn't know
// are not kept here; `resolveEventSelection` has already read those as "All". While the count
// for the current window is still loading (`countPending`), the kept pills carry no count rather
// than a 0 that may be stale; pills the data offers are left as they are.
export function withSelectedEventCategoryOptions(
  options: EventCategoryOption[],
  selectedCategoryIds: readonly string[],
  tagGroups: readonly TagGroupInfo[],
  countPending = false
): EventCategoryOption[] {
  const offered = new Set(options.map((option) => option.id))
  const kept: EventCategoryOption[] = []
  for (const id of selectedCategoryIds) {
    const group = tagGroups.find((candidate) => candidate.id === id)
    if (!group || offered.has(id)) continue
    offered.add(id)
    kept.push({
      id,
      name: group.name,
      colorHex: group.colorHex || DEFAULT_TAG_GROUP_COLOR,
      blueprintCount: 0,
      ...(countPending && { isCountPending: true }),
    })
  }
  return kept.length > 0 ? [...options, ...kept] : options
}

// The event type twin of `withSelectedEventCategoryOptions`. `options` are the types on offer
// under the selected categories (all types when none is selected). With no category selected the
// type row only opens for an applied type, and then lists just the applied ones, the way the
// Data Glossary does, rather than every type in the market.
export function withSelectedEventTypeOptions(
  options: EventTypeOption[],
  selectedTypeIds: readonly string[],
  tagGroups: readonly TagGroupInfo[],
  categoryIdByTypeId: EventTypeCategoryLookup,
  getEventTypeName: (tagId: string) => string,
  hasSelectedCategory: boolean,
  countPending = false
): EventTypeOption[] {
  if (selectedTypeIds.length === 0) return options
  const offeredById = new Map(options.map((option) => [option.id, option]))
  const zeroOptionFor = (id: string): EventTypeOption | null => {
    const categoryId = categoryIdByTypeId.get(id)
    if (!categoryId) return null
    const group = tagGroups.find((candidate) => candidate.id === categoryId)
    return {
      id,
      name: getEventTypeName(id),
      categoryId,
      colorHex: group?.colorHex || DEFAULT_TAG_GROUP_COLOR,
      blueprintCount: 0,
      ...(countPending && { isCountPending: true }),
    }
  }
  const applied = selectedTypeIds.flatMap((id) => {
    if (hasSelectedCategory && offeredById.has(id)) return []
    const option = offeredById.get(id) ?? zeroOptionFor(id)
    return option ? [option] : []
  })
  return hasSelectedCategory ? [...options, ...applied] : applied
}

// ---------------------------------------------------------------------------
// Conventional taxonomy aggregation (category / genre / subgenre chip counts)
// ---------------------------------------------------------------------------

// Per-node blueprint counts for the taxonomy filter rows, plus the catalogue
// total for the "All …" chips. Counts are keyed by conventional id.
export interface TaxonomyCounts {
  total: number
  categoryCounts: Record<string, number>
  genreCounts: Record<string, number>
  subgenreCounts: Record<string, number>
}

// Minimal shape needed to tally taxonomy membership. Matches the relevant
// fields on `BlueprintFamilyApiItem` without coupling this helper to the
// full API type.
export interface TaxonomyMembership {
  conventionalCategoryIds?: string[]
  conventionalGenreIds?: string[]
  conventionalSubgenreIds?: string[]
}

// Tallies how many blueprints belong to each conventional category / genre /
// subgenre. A blueprint can sit in several nodes (the id arrays overlap), so a
// Set dedupes per blueprint before counting. Feed this the UNSCOPED blueprint
// list so the counts (and the "All" total) stay stable as the user filters.
export function buildTaxonomyCounts(blueprints: readonly TaxonomyMembership[]): TaxonomyCounts {
  const categoryCounts: Record<string, number> = {}
  const genreCounts: Record<string, number> = {}
  const subgenreCounts: Record<string, number> = {}

  const tally = (ids: string[] | undefined, target: Record<string, number>) => {
    if (!ids) return
    for (const id of new Set(ids)) target[id] = (target[id] ?? 0) + 1
  }

  for (const bp of blueprints) {
    tally(bp.conventionalCategoryIds, categoryCounts)
    tally(bp.conventionalGenreIds, genreCounts)
    tally(bp.conventionalSubgenreIds, subgenreCounts)
  }

  return { total: blueprints.length, categoryCounts, genreCounts, subgenreCounts }
}

// React Router navigation `state` attached when the catalogue links to the
// blueprint detail page ("Open full details"). Carries the catalogue's *full*
// current search string (taxonomy + event filters, granularity, view, sort,
// window, selected blueprint) so the detail page's breadcrumb can send the user
// back to the exact catalogue view they left.
// The detail URL itself still only carries taxonomy + window (see
// `buildTaxonomyWindowSearch`) so shared links stay clean — this state is a
// best-effort extra that's naturally absent on direct links / refreshes /
// new tabs, where the detail page falls back to the narrow reconstruction.
export interface DetailPageNavState {
  catalogueBackSearch: string
  // Optional momentum snapshot from the catalogue so the detail page can skip
  // re-downloading the full momentum list on "Open full details". Ignored when
  // the detail window no longer matches `deltaWindowDays` (user changed `win`).
  deltaGames?: number | null
  // The cohort `deltaGames` was counted over, carried with it so the detail page's
  // change tile can render a percentage without refetching momentum for the divisor.
  cohortSize?: number | null
  deltaWindowDays?: CatalogueWindowDays
}

// Builds the query string (including the leading "?") that carries the
// taxonomy selection + time window between the catalogue and the blueprint
// detail pages. Param names match the URL-backed catalogue state (`category` /
// `genre` / `subgenre` / `win`), so links are shareable and both pages parse
// them with the same hook. Defaults are omitted to keep URLs clean.
export function buildTaxonomyWindowSearch(
  categoryIds: readonly string[],
  genreIds: readonly string[],
  subgenreIds: readonly string[],
  window: CatalogueWindow
): string {
  const params = new URLSearchParams()
  if (categoryIds.length) params.set('category', categoryIds.join(','))
  if (genreIds.length) params.set('genre', genreIds.join(','))
  if (subgenreIds.length) params.set('subgenre', subgenreIds.join(','))
  if (window !== DEFAULT_CATALOGUE_WINDOW) params.set('win', window)
  const query = params.toString()
  return query ? `?${query}` : ''
}

/**
 * Merges param overrides onto an existing catalogue search string (leading "?"),
 * dropping any key set to undefined. Mirrors `useMarketCatalogueState`'s
 * `patchParams` for link targets built outside the hook — a link that changes
 * one filter keeps the rest of the view (granularity, view mode, sort) intact.
 */
export function patchCatalogueSearch(search: string, patch: Record<string, string | undefined>): string {
  const params = new URLSearchParams(search)
  for (const [key, value] of Object.entries(patch)) {
    if (value) params.set(key, value)
    else params.delete(key)
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

/**
 * Link target for the event-type detail page. Kept here so every caller (tree
 * rows, hierarchy crumbs, sibling menus, the event dialog) builds the same URL,
 * and the route shape lives next to the other catalogue link builders.
 */
export function buildEventTypePath(eventTypeId: string, search = ''): string {
  return `/live-event-analytics/event-type/${eventTypeId}${search}`
}

/**
 * Link target for the event-category detail page (the event group / tag group
 * id). Same rationale as buildEventTypePath: one URL shape for every caller.
 */
export function buildEventCategoryPath(eventCategoryId: string, search = ''): string {
  return `/live-event-analytics/event-category/${eventCategoryId}${search}`
}

/**
 * Link target for the blueprint family detail page. One URL shape for every
 * caller (ranked tree, sibling menus, LET dialog trail, news feed) instead of
 * the same template string repeated.
 */
export function buildBlueprintFamilyPath(blueprintFamilyId: string, search = ''): string {
  return `/live-event-analytics/${blueprintFamilyId}${search}`
}

// Resolves the effective conventional subgenre ids for the backend scope from a
// hierarchical taxonomy selection. The backend only filters by
// `conventionalSubgenreIds`, and a category / genre is just the union of its
// subgenres, so a category or genre pick is expanded down to its subgenres.
// Deeper picks win per branch (narrow-down semantics):
//  • A category with NO selected genre under it → all of its subgenres.
//  • A selected genre with NO selected subgenre under it → all of its subgenres.
//  • An explicitly selected subgenre → just itself.
// Any explicit subgenre selection is always kept, so orphaned deep links
// (e.g. `?subgenre=…` with no parent picked) still resolve. Returns a de-duped
// list; an empty selection yields an empty array (i.e. the whole market).
//
// Without the taxonomy only the explicit subgenre picks survive, so a category /
// genre pick would silently widen to the whole market. Callers should hold their
// scoped requests until the taxonomy has loaded whenever such picks exist.
export function resolveEffectiveSubgenreIds(
  taxonomy: readonly ConventionalCategory[] | undefined,
  categoryIds: readonly string[],
  genreIds: readonly string[],
  subgenreIds: readonly string[]
): string[] {
  // Without the taxonomy tree we can only honor explicit subgenre picks.
  if (!taxonomy) return Array.from(new Set(subgenreIds))

  const categorySet = new Set(categoryIds)
  const genreSet = new Set(genreIds)
  const subgenreSet = new Set(subgenreIds)
  const result = new Set<string>(subgenreIds)

  for (const category of taxonomy) {
    const selectedGenres = category.genres.filter((genre) => genreSet.has(genre.id))

    // Category selected but not narrowed to any genre → all its subgenres.
    if (categorySet.has(category.id) && selectedGenres.length === 0) {
      for (const genre of category.genres) for (const subgenre of genre.subgenres) result.add(subgenre.id)
      continue
    }

    // Otherwise expand each selected genre, unless it is itself narrowed to
    // specific subgenres (those explicit picks are already in `result`).
    for (const genre of selectedGenres) {
      const narrowedToSubgenres = genre.subgenres.some((subgenre) => subgenreSet.has(subgenre.id))
      if (!narrowedToSubgenres) for (const subgenre of genre.subgenres) result.add(subgenre.id)
    }
  }

  return Array.from(result)
}

// The `blueprint-families` scope body scopes each family's figures, not which families
// come back, so membership is filtered client-side: a family is kept when it is filed
// under at least one subgenre of the effective scope, and an empty scope keeps the
// whole list. `keepId` is always kept (the blueprint page's own family, so its menus
// never lose their anchor).
export function filterCatalogueToScope<T extends { id: string; conventionalSubgenreIds?: readonly string[] }>(
  list: T[] | undefined,
  effectiveSubgenreIds: readonly string[],
  keepId?: string
): T[] | undefined {
  if (!list || effectiveSubgenreIds.length === 0) return list
  const scopeSet = new Set(effectiveSubgenreIds)
  return list.filter((bp) => bp.id === keepId || (bp.conventionalSubgenreIds ?? []).some((id) => scopeSet.has(id)))
}
